import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

type PageModule = { default: ComponentType }
type Preloadable = LazyExoticComponent<ComponentType> & { preload: () => Promise<PageModule> }

/**
 * lazy() with a preload handle. The cached promise is tagged with the thenable
 * status fields React inspects, so a preloaded route resolves SYNCHRONOUSLY on
 * first render — the prerendered boot never commits a blank frame.
 */
function lazyPreload(imp: () => Promise<PageModule>): Preloadable {
  let cached: (Promise<PageModule> & { status?: string; value?: PageModule; reason?: unknown }) | undefined
  const load = () => {
    if (!cached) {
      cached = imp()
      cached.then(
        (m) => { cached!.status = 'fulfilled'; cached!.value = m },
        (e) => { cached!.status = 'rejected'; cached!.reason = e },
      )
    }
    return cached
  }
  const C = lazy(load) as Preloadable
  C.preload = load
  return C
}

export const Home = lazyPreload(() => import('../../pages/Home'))
export const About = lazyPreload(() => import('../../pages/About'))
export const Paul = lazyPreload(() => import('../../pages/Paul'))
export const Blog = lazyPreload(() => import('../../pages/Blog'))
export const BlogPost = lazyPreload(() => import('../../pages/blog/BlogPost'))
export const HumanAIProject = lazyPreload(() => import('../../pages/HumanAIProject'))
export const Terms = lazyPreload(() => import('../../pages/Terms'))
export const Privacy = lazyPreload(() => import('../../pages/Privacy'))
export const Careers = lazyPreload(() => import('../../pages/Careers'))
export const NotFound = lazyPreload(() => import('../../pages/NotFound'))

/** Preload the component that will render for a pathname (boot path only). */
export function preloadFor(pathname: string): Promise<unknown> {
  if (pathname.startsWith('/blog/')) return BlogPost.preload()
  switch (pathname) {
    case '/about': return About.preload()
    case '/paul-lin': return Paul.preload()
    case '/blog': return Blog.preload()
    case '/human-ai-project':
    case '/research': return HumanAIProject.preload()
    case '/terms': return Terms.preload()
    case '/privacy': return Privacy.preload()
    case '/careers': return Careers.preload()
    case '/': return Home.preload()
    default: return NotFound.preload()
  }
}
